import React, { useRef } from 'react';
import Dropzone from 'react-dropzone';

import { showSnackbar } from 'helpers/snackbar';

import Icon from 'ds/Icon';
import Layout from 'ds/Layout';
import PillButton from 'ds/PillButton';
import Text from 'ds/Text';

export interface FileUploadDropzoneProps {
  onFileSelect: (files: File[]) => void;
  accept?: string;
  disabled?: boolean;
  maxSizeMB?: number;
  multiple?: boolean;
  helperText?: string;
  buttonText?: string;
  title?: string;
  buttonSize?: 'sm' | 'md' | 'lg';
}

const FileUploadDropzone: React.FC<FileUploadDropzoneProps> = ({
  onFileSelect,
  accept = 'application/pdf',
  disabled = false,
  maxSizeMB = 30,
  multiple = false,
  helperText = 'PDF recommended.',
  buttonText = 'Select file',
  title = 'Drag and drop file',
  buttonSize = 'sm'
}) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const hiddenFileInputRef = useRef<HTMLInputElement>(null);

  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  const showFileChooser = () => hiddenFileInputRef.current?.click();

  const validateFileSize = (file: File): boolean => {
    if (file.size > maxSizeBytes) {
      showSnackbar({
        message: `File size must be less than ${maxSizeMB}MB.`,
        negative: true
      });
      return false;
    }
    return true;
  };

  const handleDrop = (acceptedFiles: File[], rejectedFiles: any[]) => {
    setIsDragging(false);

    if (rejectedFiles.length > 0) {
      const fileTooLarge = rejectedFiles.some(rejection =>
        rejection.errors.some((error: any) => error.code === 'file-too-large')
      );
      if (fileTooLarge) {
        showSnackbar({
          message: `File size must be less than ${maxSizeMB}MB.`,
          negative: true
        });
      }
      return;
    }

    if (acceptedFiles.length > 0) {
      onFileSelect(acceptedFiles);
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const filesArray = Array.from(files);
    const validFiles = filesArray.filter(validateFileSize);

    if (validFiles.length > 0) {
      onFileSelect(validFiles);
    }

    event.target.value = '';
  };

  return (
    <>
      <Dropzone
        onDragEnter={() => setIsDragging(true)}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        accept={accept}
        disabled={disabled}
        maxSize={maxSizeBytes}
        style={{}}
      >
        <Layout
          borderRadius={16}
          paddingX={24}
          paddingY={36}
          paddingTop={24}
          paddingBottom={36}
          color="white"
          align="center"
          justify="center"
          direction="column"
          gap={12}
          cursor="pointer"
          opacity={disabled ? 0.6 : 1}
          minHeight={241}
          style={{
            position: 'relative',
            border: 'none'
          }}
        >
          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              borderRadius: 16,
              overflow: 'hidden'
            }}
          >
            <rect
              x="1"
              y="1"
              width="calc(100% - 2px)"
              height="calc(100% - 2px)"
              rx="15"
              fill="none"
              stroke={isDragging ? '#3b82f6' : '#cfd8dc'}
              strokeWidth="2"
              strokeDasharray="8 8"
            />
          </svg>
          <Layout width={64} height={64} align="center" justify="center" style={{ position: 'relative', zIndex: 1 }}>
            <Icon name="uploadCloud" size={64} color="gray-900" />
          </Layout>

          <Text size="headline-xs" bold __style={{ position: 'relative', zIndex: 1 }}>
            {title}
          </Text>

          <Layout __style={{ position: 'relative', zIndex: 1 }}>
            <PillButton
              type="purpleGradient"
              size={buttonSize}
              text={buttonText}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                showFileChooser();
              }}
              disabled={disabled}
            />
          </Layout>

          <Text size="body-xs" color="gray-400" align="center" __style={{ position: 'relative', zIndex: 1 }}>
            {helperText}
          </Text>
        </Layout>
      </Dropzone>

      <input
        ref={hiddenFileInputRef}
        type="file"
        style={{ display: 'none' }}
        accept={accept}
        multiple={multiple}
        onChange={handleFileInputChange}
      />
    </>
  );
};

export default FileUploadDropzone;
